import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { Hint, NamesInput, Stack, closeDialog, showDialog, showToast } from '@src/components';
import { useBoxRenewalError } from './useBoxRenewalError';
import { useMobileBoxLayout } from './BoxLayouts';
import type { DialogRequest } from '@src/components/Dialog/Dialog';
import { santaClient } from '@src/state/client';

type Draft = { names: string[]; problem: string | null; fail: (message: string) => void; label: (value: string) => void; setNames: (names: string[]) => void; setProblem: (problem: string | null) => void; onError: (handler: (message: string) => void) => void };

function AddUsersFields({ draft, cards, count, busy, setDisabled }: {
  draft: Draft; cards: CardInfo[]; count: number; busy: boolean; setDisabled: (disabled: boolean) => void;
}) {
  const { t } = useTranslation();
  const mobile = useMobileBoxLayout();
  const [names, setNames] = useState(draft.names);
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    draft.label(mobile && names.length > 0 ? t('organizerPanel.addUsersCount', { count: names.length }) : t('organizerPanel.addEveryone'));
  }, [draft, mobile, names.length, t]);
  useEffect(() => { draft.onError(setError); }, [draft]);
  useEffect(() => { setDisabled(Boolean(problem) || names.length === 0); }, [problem, names.length, setDisabled]);
  return <Stack gap={4}>
    <NamesInput names={names} onNamesChange={next => { draft.setNames(next); setNames(next); setError(''); }}
      label={t('game.people.addParticipantsTitle')}
      reservedNames={cards.map(card => card.username)}
      onProblem={next => { draft.setProblem(next); setProblem(next); }}
      placeholder={t('randomizer.names.placeholder')} hint={t('organizerPanel.namesHint')}
      removeLabel={name => t('randomizer.names.remove', { name })}
      rows={4} disabled={busy} testId="organizer-add-users-names" />
    <Hint tone="info" testId="organizer-add-users-count">{t('organizerPanel.existingParticipants', { count })}</Hint>
    {problem && <Hint tone="error">{t(`randomizer.problem.${problem}`)}</Hint>}
    {error && <Hint tone="error" testId="organizer-add-users-error">{error}</Hint>}
  </Stack>;
}

export function useOrganizerAddUsers(info: BoxInfo) {
  const { t } = useTranslation();
  const handleRenewalError = useBoxRenewalError(info);
  return (cards: CardInfo[], onChanged: () => Promise<void> | void) => {
    if (!info.isAdmin || info.box.isArchived) return;
    const draft: Draft = { names: [], problem: null, fail: () => {},
      setNames: names => { draft.names = names; },
      setProblem: problem => { draft.problem = problem; },
      onError: handler => { draft.fail = handler; },
      label: value => {
      if (request.submit === value) return;
      request = { ...request, submit: value };
      showDialog(request);
    } };
    let request: DialogRequest = {
      title: t('game.people.addParticipantsTitle'), mobileFullScreen: true,
      cancel: t('box.cancel'), submit: t('organizerPanel.addEveryone'),
      testId: 'organizer-add-users-dialog',
      renderBody: (busy, setDisabled) => <AddUsersFields draft={draft} cards={cards}
        count={info.participantsNumber} busy={busy} setDisabled={setDisabled} />,
      onSubmit: async () => {
        if (draft.problem || !draft.names.length) {
          draft.fail(t('organizerPanel.enterNames'));
          throw new Error('Participant names required');
        }
        try {
          const result = await santaClient.invite({ key: info.box.key,
            isInviteAfterDraw: Boolean(info.isActiveDraw), users: [], participantNames: draft.names });
          if (!result.ok) {
            handleRenewalError(result.error?.message);
            throw new Error('Invite failed');
          }
        } catch (error) {
          draft.fail(t('game.people.addFailed'));
          throw error;
        }
        // Close after creation even if refreshing fails, so retry cannot create duplicates.
        closeDialog();
        try { await onChanged(); } catch { showToast(t('game.people.addFailed')); }
      },
    };
    showDialog(request);
  };
}
